import "./CustomTimePicker.less";

import { useState, useEffect } from "react";
import { useAppSelector } from "app/hooks";
import {
    GuxDatepicker,
    GuxTimePicker,
    GuxFormFieldDropdown,
    GuxDropdown,
    GuxListbox,
    GuxOption,
} from "genesys-spark-components-react";
import { getMaxReleaseDate } from "features/scorable/scorableSlice";
import { UtilitiesApi } from "@genesys/cloud-frontend/dist/mjs/apis/UtilitiesApi";
import { useStrings } from "features/i18n/useStrings";
import strings from "./releaseEvaluationSelector.strings";
import moment from "moment-timezone";
import { ReleaseDateSelectedType } from "./ReleaseEvaluationSelector";
import { ReleaseDateOptions } from "enums/ReleaseDateOptions";
import { API_CONFIGURATION } from "util/apiUtils";

/**  NOTE : ALL DATE & TIME VALUES USED IN THIS COMPONENT WOULD BE IN LOCALTIME ZONES. 
     ONLY FOR STORING RELEASE TIME, WE WOULD ADJUST OUR LOCAL TIME W.R.T SELECTED CUSTOM TIMEZOME & STORE IT AS RELEASE TIME. 
     RELEASE TIME IS ALWAYS SENT IN APIs AS ISO STRING. **/
interface TimeZoneProps {
    name: string;
    offset: string;
}

export default function CustomTimePicker({
    releaseDateData,
    setReleaseDateData,
    timeZoneName,
}: {
    releaseDateData: ReleaseDateSelectedType;
    setReleaseDateData: (s: ReleaseDateSelectedType) => void;
    timeZoneName: string;
}) {
    const t = useStrings(strings);
    const dateTimeNow = new Date();
    const minDate = `${dateTimeNow.getFullYear()}-${String(
        dateTimeNow.getMonth() + 1
    ).padStart(2, "0")}-${String(dateTimeNow.getDate()).padStart(2, "0")}`;
    const maxDate = useAppSelector(getMaxReleaseDate);
    const timeNow = `${String(dateTimeNow.getHours()).padStart(
        2,
        "0"
    )}:${String(dateTimeNow.getMinutes()).padStart(2, "0")}:${String(
        dateTimeNow.getSeconds()
    ).padStart(2, "0")}`;

    const [timeZoneList, setTimeZoneList] = useState<
        Record<string, TimeZoneProps>
    >({});
    const [customDateString, setCustomDateString] = useState(minDate);
    const [customTimeString, setCustomTimeString] = useState(timeNow);
    const [selectedTimeZone, setSelectedTimeZone] = useState(timeZoneName);
    const [timezoneLoaded, setTimezoneLoaded] = useState(false);

    function getFormattedNameAndOffset(name: string, offset: number) {
        const hrs = Math.floor(Math.abs(offset) / 60);
        const mins = Math.abs(offset) % 60;
        const sign = offset >= 0 ? "+" : "-";
        const formattedOffset = `${sign}${String(hrs).padStart(
            2,
            "0"
        )}:${String(mins).padStart(2, "0")}`;
        const formattedName = `${name} (${formattedOffset})`;
        return { formattedName, formattedOffset };
    }

    useEffect(() => {
        setReleaseDateData({
            type: ReleaseDateOptions.CUSTOM,
            time: moment(dateTimeNow).tz(selectedTimeZone, true).toDate(),
        });
    }, []);

    useEffect(() => {
        if (releaseDateData.type === ReleaseDateOptions.CUSTOM) {
            const utilitiesApi = new UtilitiesApi(API_CONFIGURATION);
            utilitiesApi
                .getTimezones({ pageSize: 1000, pageNumber: 1 })
                .then((result) => {
                    const zones: Record<string, TimeZoneProps> = {};
                    result.entities?.map((zone) => {
                        if (zone.id) {
                            const { formattedName, formattedOffset } =
                                getFormattedNameAndOffset(
                                    zone.id ?? "",
                                    zone.offset ?? 0
                                );
                            zones[zone.id] = {
                                name: formattedName,
                                offset: formattedOffset,
                            };
                        }
                    });
                    setTimeZoneList(zones);
                });
        }
    }, [releaseDateData]);

    //newDate here is in localTimeZone and isn't expected to be adjusted w.r.t the selected custom timezone.
    //As a rule of thumb, we set release date via setReleaseDate, after adjusting custom time w.r.t selected time zone
    const dateUpdated = (newDate: Date) => {
        // check if it is a date in the future
        if (newDate.getTime() >= dateTimeNow.getTime()) {
            const timeZoneAdjustedNewTime = moment(newDate)
                .tz(selectedTimeZone, true)
                .toDate();
            setReleaseDateData({
                type: ReleaseDateOptions.CUSTOM,
                time: timeZoneAdjustedNewTime,
            });
        } else {
            // set date back to current time
            setCustomDateString(minDate);
            setCustomTimeString(timeNow);
            setReleaseDateData({
                type: ReleaseDateOptions.CUSTOM,
                time: moment(dateTimeNow).tz(selectedTimeZone, true).toDate(),
            });
        }
    };

    return (
        <div className="CustomTimePicker">
            {maxDate ? (
                <GuxDatepicker
                    className="GuxDatePicker"
                    minDate={minDate}
                    maxDate={maxDate}
                    value={customDateString}
                    onInput={({ target }) => {
                        if (target.value) {
                            setCustomDateString(target.value);
                            const newDate = moment(
                                target.value + " " + customTimeString
                            ).toDate();
                            dateUpdated(newDate);
                        }
                    }}
                />
            ) : (
                <GuxDatepicker
                    className="GuxDatePicker"
                    minDate={minDate}
                    value={customDateString}
                    onInput={({ target }) => {
                        if (target.value) {
                            setCustomDateString(target.value);
                            const newDate = moment(
                                target.value + " " + customTimeString
                            ).toDate();
                            dateUpdated(newDate);
                        }
                    }}
                />
            )}
            <GuxTimePicker
                className="GuxTimePicker"
                interval={15}
                value={`${customTimeString.split(":")[0]}:${
                    customTimeString.split(":")[1]
                }`}
                onInput={(e) => {
                    const selectedValue = (e.target as HTMLInputElement).value;
                    if (selectedValue) {
                        setCustomTimeString(selectedValue);
                        const newDate = moment(
                            customDateString + " " + selectedValue
                        ).toDate();
                        dateUpdated(newDate);
                    }
                }}
            />
            <GuxFormFieldDropdown className="TimeZonePicker">
                <label slot="label">{t.labels.timezone}</label>
                <GuxDropdown
                    onClick={() => setTimezoneLoaded(true)}
                    filter-type="starts-with"
                    value={selectedTimeZone}
                    onInput={(e) => {
                        const newTimeZone = (e.target as HTMLGuxDropdownElement)
                            .value;
                        if (newTimeZone) {
                            setSelectedTimeZone(newTimeZone);
                            const customTime = moment(
                                customDateString + " " + customTimeString
                            ).toDate();
                            const newDate = moment(customTime)
                                .tz(newTimeZone, true)
                                .toDate();
                            setReleaseDateData({
                                type: ReleaseDateOptions.CUSTOM,
                                time: newDate,
                            });
                        }
                    }}
                    placeholder={selectedTimeZone}
                    data-testid="time-zone-dropdown"
                >
                    <GuxListbox>
                        {timezoneLoaded ? (
                            Object.entries(timeZoneList).map(
                                ([key, element]) => {
                                    return (
                                        <GuxOption value={key} key={key}>
                                            {element.name}
                                        </GuxOption>
                                    );
                                }
                            )
                        ) : (
                            <GuxOption
                                value={selectedTimeZone}
                                key={selectedTimeZone}
                            >
                                {selectedTimeZone}
                            </GuxOption>
                        )}
                    </GuxListbox>
                </GuxDropdown>
            </GuxFormFieldDropdown>
        </div>
    );
}
